import type { ErrorEnvelope } from '@tabita/contracts';

/**
 * The HTTP client.
 *
 * Everything the client knows about talking to TABITA lives here: the token,
 * the error shape, and the refresh dance. Components call typed functions and
 * never touch fetch.
 */
export const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';

/**
 * An error carrying the API's machine-readable code.
 *
 * The code is what the UI branches on and what it looks a translated message up
 * by; the English `message` from the server is for developers and is never
 * rendered verbatim.
 */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details: Record<string, unknown> = {},
    readonly correlationId: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field-level validation failures, keyed by dotted path. */
  get fieldErrors(): Record<string, string> {
    const fields = this.details.fields;
    if (!Array.isArray(fields)) return {};

    return Object.fromEntries(
      fields
        .filter(
          (f): f is { path: string; message: string } =>
            typeof f === 'object' && f !== null && 'path' in f && 'message' in f,
        )
        .map((f) => [f.path, f.message]),
    );
  }

  /**
   * `auth.passwordPolicy.*` i18n keys from a failed password-policy check
   * (`assertPasswordMeetsPolicy`) — a distinct error shape from `fieldErrors`
   * above, since this check runs as a domain rule, not a DTO field
   * validation, so its `details` carries `violations`, not `fields`.
   */
  get passwordViolations(): readonly string[] {
    const violations = this.details.violations;
    return Array.isArray(violations)
      ? violations.filter((v): v is string => typeof v === 'string')
      : [];
  }
}

/**
 * Token storage.
 *
 * `sessionStorage` by default: a token that survives closing the tab outlives
 * the user's intent, and church staff routinely share machines. "Remember
 * me" on the login form is what moves a session into `localStorage` instead
 * — an explicit choice the user makes on their own device, not a default
 * that would defeat the shared-machine reasoning above. The refresh token is
 * genuinely valid for `JWT_REFRESH_TTL` (30 days) regardless of where it is
 * kept; only where the browser keeps it changes.
 *
 * Neither is safe against XSS. The real mitigation is that the API sets a
 * strict CSP and the client renders no user-supplied HTML.
 */
const ACCESS_KEY = 'tabita.accessToken';
const REFRESH_KEY = 'tabita.refreshToken';
const REMEMBER_KEY = 'tabita.rememberMe';

/** Reads the marker from whichever store might hold it — cleared sessions
 *  leave neither, so this never silently picks the wrong backend. */
function activeStorage(): Storage {
  try {
    if (localStorage.getItem(REMEMBER_KEY) === '1') return localStorage;
  } catch {
    /* falls through to sessionStorage */
  }
  return sessionStorage;
}

export const tokenStore = {
  get access(): string | null {
    try {
      return activeStorage().getItem(ACCESS_KEY);
    } catch {
      return null;
    }
  },
  get refresh(): string | null {
    try {
      return activeStorage().getItem(REFRESH_KEY);
    } catch {
      return null;
    }
  },
  get rememberMe(): boolean {
    try {
      return localStorage.getItem(REMEMBER_KEY) === '1';
    } catch {
      return false;
    }
  },
  /** `remember` is only read on a fresh sign-in — a token refresh keeps
   *  whatever storage the session already started in. */
  set(access: string, refresh: string, remember?: boolean): void {
    try {
      const store =
        remember === undefined ? activeStorage() : remember ? localStorage : sessionStorage;
      store.setItem(ACCESS_KEY, access);
      store.setItem(REFRESH_KEY, refresh);
      if (remember !== undefined) {
        if (remember) localStorage.setItem(REMEMBER_KEY, '1');
        else localStorage.removeItem(REMEMBER_KEY);
      }
    } catch {
      // Blocked storage means the session lasts until reload. Better than
      // failing the sign-in outright.
    }
  },
  clear(): void {
    for (const store of [localStorage, sessionStorage]) {
      try {
        store.removeItem(ACCESS_KEY);
        store.removeItem(REFRESH_KEY);
        store.removeItem(REMEMBER_KEY);
      } catch {
        /* nothing to clear */
      }
    }
  },
};

/**
 * Serialises concurrent refreshes.
 *
 * Without this, five queries failing with 401 at once would each present the
 * same refresh token. The first rotates it; the other four then present an
 * already-rotated token, which the server correctly treats as theft and
 * responds to by killing the session. The user gets signed out by their own
 * dashboard loading.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    const refreshToken = tokenStore.refresh;
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        tokenStore.clear();
        return false;
      }

      const tokens = (await response.json()) as { accessToken: string; refreshToken: string };
      tokenStore.set(tokens.accessToken, tokens.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  readonly query?: Record<string, string | number | boolean | null | undefined>;
  /** Set for login and refresh, which have no token to send. */
  readonly anonymous?: boolean;
  readonly signal?: AbortSignal;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);

  for (const [key, value] of Object.entries(options.query ?? {})) {
    // `null` and `undefined` are omitted rather than sent as the strings
    // "null" and "undefined", which the API would reject as invalid.
    if (value !== null && value !== undefined) {
      url.searchParams.set(key, String(value));
    }
  }

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';

    const token = tokenStore.access;
    if (!options.anonymous && token) headers.Authorization = `Bearer ${token}`;

    return fetch(url.toString(), {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  };

  let response = await send();

  // One retry after a successful refresh. A second 401 means the session is
  // genuinely gone.
  if (response.status === 401 && !options.anonymous) {
    if (await refreshTokens()) {
      response = await send();
    }
  }

  if (response.status === 204) {
    return undefined as T;
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  return (await response.json()) as T;
}

async function toApiError(response: Response): Promise<ApiError> {
  try {
    const envelope = (await response.json()) as ErrorEnvelope;
    return new ApiError(
      envelope.error.code,
      envelope.error.message,
      response.status,
      envelope.error.details,
      envelope.error.correlationId,
    );
  } catch {
    // A non-JSON body means something upstream failed — a proxy, a gateway
    // timeout. Not a TABITA error envelope.
    return new ApiError('INTERNAL_ERROR', response.statusText, response.status);
  }
}
